import * as Sentry from '@sentry/react'
import { bootstrapSupportSession, getSupportSessionStatus, isSupportSession, readAndStripFragment } from './lib/supportSession'
import { scrubSentryEvent, scrubSentryBreadcrumb } from './lib/sentryUtils'
import {
  initAnalytics,
  captureAttribution,
  registerWebVitals,
  disableAnalyticsForPage,
} from './lib/analytics'
import { initFirebaseRum } from './lib/firebaseRum'

// EP-160 section 2.3: FIRST statement. Reads and strips the support console's
// `#support_session=<code>` (or this tab's stored session) before anything
// that records a URL starts: PostHog, Firebase RUM and Sentry below.
// Browser only; the operator app never reads the fragment.
readAndStripFragment()
const supportTab = isSupportSession()
// A support tab sends no client analytics at all, for the page's lifetime.
if (supportTab) disableAnalyticsForPage()

// C1 — init posthog-js with hardened settings before the React tree renders.
// opt_out_capturing_by_default is true; C2 consent gate opts in after checking
// the .flowrent.io parent-domain cookie inside initAnalytics → applyConsent.
// NEVER remove the <Suspense> below — it gates Unleash flag initialization.
initAnalytics()

// EP-88 T108b — Firebase Analytics RUM. Fire-and-forget: async guard inside
// initFirebaseRum means no-op when VITE_FIREBASE_* config is absent.
if (!supportTab) {
  initFirebaseRum().catch(() => {
    // Non-fatal: if Firebase Analytics fails to initialize, silently skip.
  })
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN_WEB,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  tracesSampleRate: 0.2,
  integrations: [Sentry.browserTracingIntegration()],
  beforeSend: scrubSentryEvent,
  // WEB-M11: scrub breadcrumbs at creation — axios error messages/URLs can
  // embed oauth/token requests carrying refresh_token=.
  beforeBreadcrumb: scrubSentryBreadcrumb,
})

import { Spinner } from '@/components/ui/Spinner'
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import FlagProvider from '@unleash/proxy-client-react'
import { AuthProvider } from '@/contexts/AuthContext'
import App from './App'
import { ErrorFallback } from '@/components/ErrorFallback'
import './i18n'
import './index.css'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { queryClient } from '@/lib/queryClient'
import { initCrashlytics } from '@/lib/crashlytics'
import { SupportSessionGate } from '@/components/SupportSessionGate'
import { useTranslation } from 'react-i18next'

const unleashConfig = {
  url: import.meta.env.VITE_UNLEASH_URL || 'https://unleash.kanoonth.com/api/frontend',
  clientKey: import.meta.env.VITE_UNLEASH_FRONTEND_KEY || '',
  appName: 'flowrent-web',
  refreshInterval: 15,
}

if (Capacitor.isNativePlatform()) {
  // Mark the native shell so global WKWebView touch CSS applies (index.css
  // `.native-shell`) — suppresses the long-press callout/selection + tap delay
  // that broke hamburger/nav/CTA taps and input focus (B-033/B-034/B-035/B-036).
  document.documentElement.classList.add('native-shell')
  // Status bar: dark text on light background, matching the daisyUI Peerapat theme.
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {})
  StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {}) // Android only; no-op on iOS
  // Hide the splash screen with a smooth fade once React is initialized.
  SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {})
  // EP-88 T108a: native mobile crash reporting + Analytics via Capacitor plugins.
  // Uses the native Firebase SDKs — INDEPENDENT of T108b's Firebase JS SDK init.
  // No-ops if google-services.json / GoogleService-Info.plist are not provisioned.
  initCrashlytics().catch(() => {})
}

// Fallback rendered while the Unleash flag-loading promise (thrown by useFeatureFlags)
// is pending. Public routes should not block on flag resolution, so we return null
// for /login and /sign/* to give those pages immediate first contentful paint.
// NEVER remove the <Suspense> — it gates Unleash flag initialization for the whole app.
// If you add a new public route that should not wait for flags, extend the check below.
//
// B-021 (Android): read the route via useLocation(), NOT window.location.pathname.
// During the post-login navigate('/') the window pathname can still read '/login'
// for the render that suspends, so the old window-based check returned null → a
// blank white screen with no spinner after login (which only "recovered" on a
// manual reload, because then the path was cleanly '/'). useLocation reflects the
// navigation target synchronously, so the protected route always shows the spinner.
// eslint-disable-next-line react-refresh/only-export-components -- single-file app entry
function LoadingShell() {
  const { pathname } = useLocation()
  if (
    pathname.startsWith('/login') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/sign/') ||
    pathname.startsWith('/parking/') ||
    pathname.startsWith('/visit/')
  ) {
    return null
  }
  return <BrandedSpinner />
}

// QA B-037: a branded shell so the post-login transition reads as an
// intentional load, not a blank/hung grey screen. Also shown while a support
// tab exchanges its code (EP-160), before the router exists.
// eslint-disable-next-line react-refresh/only-export-components -- single-file app entry
function BrandedSpinner({ label }: { label?: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-base-200">
      <span className="text-2xl font-bold text-primary tracking-tight">FlowRent</span>
      <Spinner size="xl" className="text-primary" aria-label={label} />
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- single-file app entry
function SupportSessionStarting() {
  const { t } = useTranslation('common')
  return <BrandedSpinner label={t('support_session.starting')} />
}

async function start() {
  const root = createRoot(document.getElementById('root')!)

  // EP-160 section 2.3: exchange the console's code for the session token
  // before AuthProvider renders, so its first request already carries it.
  if (getSupportSessionStatus() === 'starting') {
    root.render(<SupportSessionStarting />)
    await bootstrapSupportSession()
  }

  root.render(
    <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error as Error} />}>
      <StrictMode>
        <SupportSessionGate>
          <FlagProvider config={unleashConfig}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <AuthProvider>
                  <Suspense fallback={<LoadingShell />}>
                    <App />
                  </Suspense>
                </AuthProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </FlagProvider>
        </SupportSessionGate>
      </StrictMode>
    </Sentry.ErrorBoundary>
  )

  // No client analytics in a support tab (EP-160).
  if (supportTab) return

  // C4: capture UTM / _gl attribution + initial $pageview on first app landing.
  // Runs after render so the DOM exists; consent-gated by posthog-js internally.
  captureAttribution()

  // C3: register web-vitals collectors (consent-gated).
  // Dynamic import deferred past the initial render (see registerWebVitals for details).
  registerWebVitals().catch(() => {
    // Non-fatal: if web-vitals fails to load (e.g. old browser), silently skip.
  })
}

void start()
